import { ApplicationRef, ChangeDetectionStrategy, Component, effect, ElementRef, HostBinding, HostListener, inject, input, output, ViewChildren, QueryList } from '@angular/core';
import { FikoYTKokBilesenComponent } from '../../../../../kok-bilesen/kok-bilesen.component';
import { IKart, TurGizlilikBilgi, IIfadeTuru, IfadeBilgi, IEkTuru } from '../../../../interfaces/IKart.interface';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ParametrelerComponent } from '../../parametreler/parametreler.component';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
import { IceriklerComponent } from './icerikler/icerikler.component';
import { KartMenuComponent } from './kart-menu/.component';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { RouterModule } from '@angular/router';
import { DialogGizlilikSeciciComponent } from '../dialog-gizlilik-secici/dialog-gizlilik-secici.component';
import { DialogEtiketlerComponent } from '../dialog-etiketler/dialog-etiketler.component';
import { DialogKisiSeciciComponent } from '../dialog-kisi-secici/dialog-kisi-secici.component';
import { FikoYTBayrakEditor } from '../bayrak-editor/.component';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { IKartTuru } from '../../../../interfaces/IKart.interface';
import { KisiBalonV2Component } from '@/src/app/shared/components/kisi-balon/kisi-balon.component';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { ServisUyarProjeler } from '@/src/app/uygulamalar/uyar-dokum/projeler/projeler.service';
import { IKartComponent } from '../paylas-kutusu/IKart/ikart-kutusu.component';
import { PaylasKutusuComponent } from '../paylas-kutusu/paylas-kutusu.component';
import { IParametreSetiGrubu, IParametreSeti } from '../../../../interfaces/IParametreSeti.interface';
import { CanliGuncellemeService } from '../../dinamik-bilesen/canli-guncelleme.service';
import { AltKartHesaplamaService } from '../../dinamik-bilesen/alt-kart-hesaplama.service';
import { KartBildirimService } from '../../dinamik-bilesen/kart-bildirim.service';


@Component({
  selector: 'fikoyt-kart-kok',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, FormsModule, RouterModule, NzButtonModule, NzInputModule, ParametrelerComponent, NzWaveModule, IceriklerComponent, NzDividerModule, NzPopconfirmModule, KisiBalonV2Component, NzPopoverModule, IKartComponent, PaylasKutusuComponent,],
  templateUrl: "./fikoyt-kart.component.html",
  styleUrl: "./fikoyt-kart.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FikoYTKartKokComponent extends FikoYTKokBilesenComponent {
  /** 📢 premium özellik lisansı (köprü yoksa AÇIK). */
  lisans(alt: string): boolean { try { return (window as any).__fikoLisans?.('📢', alt) ?? true; } catch { return true; } }

  kart = input<IKart<any>>(null);
  kartParametreSetleri = input<IParametreSetiGrubu[]>([]);
  sayfaBalonDetayCekFn = input<(id: string) => Promise<{ kisiDetay: any; iliskiler?: any[] }>>();
  kartGuncellendi = output<IKart>();
  private servisProjeler = inject(ServisUyarProjeler);
  private elRef = inject(ElementRef);
  private appRef = inject(ApplicationRef);
  private canliGuncelleme = inject(CanliGuncellemeService);
  private altKartHesaplama = inject(AltKartHesaplamaService);
  private kartBildirim = inject(KartBildirimService);

  // Sabitlenmiş kart host class'ı
  @HostBinding('class.sabitlendi') get miSabitlendi(): boolean {
    return this.kart()?.sabitlendi === true;
  }

  // İfade türleri ve bilgileri
  readonly IIfadeTuru = IIfadeTuru;
  readonly IfadeBilgi = IfadeBilgi;

  get gizlilikSimgeSinif(): string | undefined {
    return TurGizlilikBilgi[this.kart()?.gizlilik]?.simgeSinif;
  };

  get miSahip(): boolean {
    return this.FikoYT.kimlik.aktifKullaniciJson()?._id === this.kart()?.sahip;
  }

  // ESKI beğenme sistemi - geriye dönük uyumluluk için
  get miBegen(): boolean {
    const kullaniciId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;
    return this.kart()?.begenenler?.includes(kullaniciId) ?? false;
  }

  get begenmeSayisi(): number {
    return this.kart()?.begenenler?.length ?? 0;
  }

  // YENİ ifade sistemi
  get aktifIfadem(): IIfadeTuru | null {
    const kullaniciId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;
    const benimIfadem = this.kart()?.ifadeler?.find(i => i.sahip === kullaniciId);
    return benimIfadem?.turu ?? null;
  }

  get toplamIfadeSayisi(): number {
    return this.kart()?.ifadeler?.length ?? 0;
  }

  get enPopulerIfade(): { turu: IIfadeTuru; sayi: number } | null {
    const sayilar = this.kart()?.ifadeSayilari;
    if (!sayilar) return null;

    let enYuksek = 0;
    let enPopuler: IIfadeTuru | null = null;

    Object.entries(sayilar).forEach(([turu, sayi]) => {
      if (sayi > enYuksek) {
        enYuksek = sayi;
        enPopuler = turu as IIfadeTuru;
      }
    });

    return enPopuler ? { turu: enPopuler, sayi: enYuksek } : null;
  }

  ifadeSayisi(turu: IIfadeTuru): number {
    return this.kart()?.ifadeSayilari?.[turu] ?? 0;
  }

  // Beğenenleri aggregate ile hesapla
  private async begenenlerListesiniGuncelle(kartId: string): Promise<string[]> {
    try {
      const begenenlerIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        parametreler: {
          aggregate: [
            {
              $match: {
                vt: "FikoYT.sistem.hareketler",
                yol: "FikoYT.sistem.hareketler.begen",
                veri: kartId,
                silindi: { $ne: true }
              }
            },
            {
              $group: {
                _id: null,
                begenenler: { $push: "$sahip" }
              }
            }
          ]
        },
        onEkOlsun: true,
        depoModu: false,
      });

      const cevap = await begenenlerIstek.isteSync();
      const sonuc = cevap?.cevap?.sonuc?.[0];
      return sonuc?.begenenler ?? [];
    } catch (e) {
      console.warn('Beğenenler listesi alınamadı:', e);
      return [];
    }
  }

  private _sonYeFikolaKartId: string = null;

  constructor() {
    super();

    // Kart yüklendiğinde gerçek YeFikola sayısını DB'den çek
    // 🚀 PERFORMANS: Feed (paylasim-sistemi) bu sayacı zaten tek $lookup ile toplu
    // hesaplayıp gönderiyor. Bu effect sadece FALLBACK: feed dışı tek-kart
    // görünümlerinde yeFikolaSayisi boş geldiyse ayrı aggregate ile tamamla
    // (aksi halde her kart için ayrı istek = N+1 tekrar oluşurdu).
    effect(() => {
      const k = this.kart();
      if (k?._id && k._id !== this._sonYeFikolaKartId) {
        this._sonYeFikolaKartId = k._id;
        if (k.yeFikolaSayisi != null) return;
        // Mevcut değeri koru (flicker önleme) — aggregate sonucu gelince güncelle
        this.yeFikolaSayisiniGuncelle(k._id).then(sayi => {
          if (k.yeFikolaSayisi !== sayi) {
            k.yeFikolaSayisi = sayi;
            this.CD.detectChanges();
          }
        });
      }
    });
  }

  parametreProxy(parametreler: any[]): any {
    return new Proxy({}, {
      get(_, prop: string) {
        return parametreler.find(p => p.ismi === prop)?.degeri ?? null;
      },
      set(_, prop: string, value) {
        const param = parametreler.find(p => p.ismi === prop);
        if (param) {
          param.degeri = value;
          return true;
        }
        return false; // yoksa hiçbir şey yapma
      }
    });
  }

  /** NOT: YeFikola (Repost/Paylaş) */
  tiklandiYeFikola() {
    const kartId = this.kart()?._id;
    if (!kartId) return;

    this.servisProjeler.icerikEkle({
      _id: this.FikoYT.fikoIdUret(),
      turu: IEkTuru.REFERANS,
      rid: kartId
    });
    this.servisProjeler.aktifEtSonSayfa();

    this.FikoYT.degisti.emit();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    this.nzMessage.success('Paylaş kutusuna eklendi');
  }

  /** Gerçek YeFikola sayısını DB'den çek (bu kartı referans alan icerik sayısı) */
  private async yeFikolaSayisiniGuncelle(kartId: string): Promise<number> {
    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        parametreler: {
          aggregate: [
            {
              $match: {
                "ek.turu": "REFERANS",
                "ek.rid": kartId,
                _uid: { $exists: true },
                silindi: { $ne: true }
              }
            },
            {
              $lookup: {
                from: "hareketler",
                localField: "_uid",
                foreignField: "_id",
                as: "_ustKart"
              }
            },
            { $unwind: "$_ustKart" },
            { $match: { "_ustKart.silindi": { $ne: true } } },
            { $count: "toplam" }
          ]
        },
        onEkOlsun: true,
        depoModu: false,
      });

      const cevap = await istek.isteSync();
      const toplam = cevap?.cevap?.sonuc?.[0]?.toplam ?? 0;
      return toplam;
    } catch (e) {
      console.warn('YeFikola sayısı alınamadı:', e);
      return 0;
    }
  }

  // İfade meşgul durumu
  ifadeMesgul = false;
  ifadeSecimAcik = false;
  ifadeBurstTuru: IIfadeTuru | null = null;
  duzenlemeMesgul = false;

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.ifadeSecimAcik) {
      this.ifadeSecimAcik = false;
      this.CD.detectChanges();
    }
  }

  /** NOT: İfade Seçimi */
  tiklandiIfadeMenu() {
    this.ifadeSecimAcik = !this.ifadeSecimAcik;
    this.CD.detectChanges();
  }

  /** NOT: İfade İşlemleri */
  async tiklandiIfade(turu: IIfadeTuru) {
    if (this.ifadeMesgul) return;

    const kartId = this.kart()?._id;
    const kullaniciId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;

    if (!kartId || !kullaniciId) return;

    // Burst animasyonu tetikle
    this.ifadeBurstTuru = turu;
    this.CD.detectChanges();
    setTimeout(() => {
      this.ifadeBurstTuru = null;
      this.CD.detectChanges();
    }, 400);

    this.ifadeMesgul = true;
    this.ifadeSecimAcik = false;
    this.CD.detectChanges();

    const oncekiIfadem = this.aktifIfadem;

    // Optimistic UI Update
    if (!this.kart().ifadeler) this.kart().ifadeler = [];
    if (!this.kart().ifadeSayilari) {
      this.kart().ifadeSayilari = {} as Record<IIfadeTuru, number>;
    }

    // Önceki ifadeyi kaldır
    if (oncekiIfadem) {
      this.kart().ifadeler = this.kart().ifadeler.filter(i => i.sahip !== kullaniciId);
      this.kart().ifadeSayilari[oncekiIfadem] = Math.max(0, (this.kart().ifadeSayilari[oncekiIfadem] ?? 0) - 1);
    }

    // Aynı ifadeye tıklandıysa kaldır, farklıysa ekle
    if (oncekiIfadem !== turu) {
      this.kart().ifadeler.push({
        sahip: kullaniciId,
        turu,
        tarih: new Date()
      });
      this.kart().ifadeSayilari[turu] = (this.kart().ifadeSayilari[turu] ?? 0) + 1;
    }

    this.CD.detectChanges();

    try {
      if (oncekiIfadem) {
        // Önceki ifade kaydını sil
        const kontrolIstek = new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.aggregate',
          parametreler: {
            aggregate: [
              {
                $match: {
                  vt: "FikoYT.sistem.hareketler",
                  yol: "FikoYT.sistem.hareketler.ifade",
                  sahip: kullaniciId,
                  veri: kartId,
                  silindi: { $ne: true }
                }
              }
            ]
          },
          onEkOlsun: true,
          depoModu: false,
        });

        const kontrolCevap = await kontrolIstek.isteSync();
        const mevcutKayit = kontrolCevap?.cevap?.sonuc?.[0];

        if (mevcutKayit?._id) {
          const formData = new FormData();
          formData.append('req_body', JSON.stringify({
            _id: mevcutKayit._id,
            silindi: true
          }));

          const silIstek = new FikoYTIstekKok({
            metod: 'POST',
            komut: 'FikoYT.sistem.DB.hareketler.ekle',
            parametreler: formData,
            onEkOlsun: true,
            depoModu: false,
          });

          await silIstek.isteSync();
        }
      }

      // Aynı ifadeye tıklanmadıysa yeni kayıt oluştur
      if (oncekiIfadem !== turu) {
        const ifadeKayit: IKart<any> = {
          vt: "FikoYT.sistem.hareketler",
          yol: "FikoYT.sistem.hareketler.ifade",
          sahip: kullaniciId,
          silindi: false,
          turu: IKartTuru.SISTEM,
          veri: kartId,
          tarih: new Date(),
          ayarlar: { ifadeTuru: turu }
        };

        const formData = new FormData();
        formData.append('req_body', JSON.stringify(ifadeKayit));

        const ekleIstek = new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.ekle',
          parametreler: formData,
          onEkOlsun: true,
          depoModu: false,
        });

        await ekleIstek.isteSync();
      }

      // İfadeleri aggregate ile güncelle
      const guncelIfadeler = await this.ifadelerListesiniGuncelle(kartId);
      this.kart().ifadeler = guncelIfadeler.ifadeler;
      this.kart().ifadeSayilari = guncelIfadeler.sayilar;

      // Güncel ifadeleri kart belgesine kaydet
      await this.kartGuncelle({
        ifadeler: guncelIfadeler.ifadeler,
        ifadeSayilari: guncelIfadeler.sayilar
      });


    } catch (e) {
      console.error('İfade hatası:', e);

      // Rollback - Optimistic UI'ı geri al
      if (oncekiIfadem) {
        this.kart().ifadeler.push({
          sahip: kullaniciId,
          turu: oncekiIfadem,
          tarih: new Date()
        });
        this.kart().ifadeSayilari[oncekiIfadem] = (this.kart().ifadeSayilari[oncekiIfadem] ?? 0) + 1;
      }

      if (oncekiIfadem !== turu) {
        this.kart().ifadeler = this.kart().ifadeler.filter(i => i.sahip !== kullaniciId || i.turu !== turu);
        this.kart().ifadeSayilari[turu] = Math.max(0, (this.kart().ifadeSayilari[turu] ?? 0) - 1);
      }

      this.nzMessage.error('İfade işlemi başarısız oldu');
    } finally {
      setTimeout(() => {
        this.ifadeMesgul = false;
        this.CD.detectChanges();
        this.FikoYT.degisti.emit();
      }, 0);
    }
  }

  // İfadeleri aggregate ile hesapla
  private async ifadelerListesiniGuncelle(kartId: string): Promise<{ ifadeler: any[]; sayilar: Record<IIfadeTuru, number> }> {
    try {
      const ifadelerIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        parametreler: {
          aggregate: [
            {
              $match: {
                vt: "FikoYT.sistem.hareketler",
                yol: "FikoYT.sistem.hareketler.ifade",
                veri: kartId,
                silindi: { $ne: true }
              }
            }
          ]
        },
        onEkOlsun: true,
        depoModu: false,
      });

      const cevap = await ifadelerIstek.isteSync();
      const ifadeKayitlari = cevap?.cevap?.sonuc ?? [];

      // İfadeleri dönüştür
      const ifadeler = ifadeKayitlari.map(k => ({
        sahip: k.sahip,
        turu: k.ayarlar?.ifadeTuru,
        tarih: k.tarih
      }));

      // Sayıları hesapla
      const sayilar: Record<IIfadeTuru, number> = {} as Record<IIfadeTuru, number>;
      Object.values(IIfadeTuru).forEach(turu => {
        sayilar[turu] = ifadeler.filter(i => i.turu === turu).length;
      });

      return { ifadeler, sayilar };
    } catch (e) {
      console.warn('İfadeler listesi alınamadı:', e);
      return { ifadeler: [], sayilar: {} as Record<IIfadeTuru, number> };
    }
  }

  // Alt Kartlar (Yorumlar)
  gosterAltKartlarButon = input(true);
  altKartlarAcik = false;
  altKartlar: IKart<any>[] = [];
  altKartlarYukleniyor = false;
  yeniAltKartMetni = '';
  altKartGonderiliyor = false;
  /** Alt kart ekleme modunu genişlet (paylaş kutusu ile) */
  altKartGenisMod = false;

  /**
   * Ana kartın seçili parametre setinden alt kart şablonlarını çıkarır.
   * kart().veri._parametreSetleri → set id → altKartSablonlari
   */
  get altKartSablonlari(): IParametreSetiGrubu[] {
    const kart = this.kart();
    const setIdleri = new Set<string>();
    const bilesenIdleri = new Set<string>();

    const setMap = kart?.veri?._parametreSetleri;
    if (setMap) Object.values(setMap).forEach((v: any) => { if (v) setIdleri.add(v); });

    const icerikler = kart?.veri?.icerikler;
    if (icerikler?.length) {
      icerikler.forEach((ic: any) => {
        if (ic?.ek?._secilenSetId) setIdleri.add(ic.ek._secilenSetId);
        if (ic?.ek?.bilesen) bilesenIdleri.add(ic.ek.bilesen);
      });
    }
    if (setIdleri.size === 0 && bilesenIdleri.size === 0) return [];

    const tumSablonlar: IParametreSeti[] = [];
    const tumSetler = [...(this.kartParametreSetleri() ?? []), ...(this.servisProjeler?.parametreSetleri ?? [])];

    for (const grup of tumSetler) {
      const eslesiyor = setIdleri.has(grup.id) || ((grup as any).bilesen && bilesenIdleri.has((grup as any).bilesen));
      if (eslesiyor) {
        if ((grup as any).altKartSablonlari?.length) tumSablonlar.push(...(grup as any).altKartSablonlari);
        for (const alt of (grup.altGruplar ?? [])) {
          if (alt.altKartSablonlari?.length) tumSablonlar.push(...alt.altKartSablonlari);
        }
      }
      if (!eslesiyor) {
        for (const alt of (grup.altGruplar ?? [])) {
          const altEslesiyor = setIdleri.has(alt.id) || ((alt as any).bilesen && bilesenIdleri.has((alt as any).bilesen));
          if (altEslesiyor) {
            if (alt.altKartSablonlari?.length) tumSablonlar.push(...alt.altKartSablonlari);
          }
        }
      }
    }

    if (tumSablonlar.length === 0) return [];

    return [{
      id: 'alt-kart-sablonlari',
      baslik: 'Alt Kart Şablonları',
      simge: '📋',
      kaynak: 'kod' as const,
      altGruplar: tumSablonlar,
    }];
  }

  /** Alt kart paylaş kutusu için kart nesnesi */
  _altKartPaylasKart: IKart = null;
  get altKartPaylasKart(): IKart {
    if (!this._altKartPaylasKart || this._altKartPaylasKart._uid !== this.kart()?._id) {
      const ana = this.kart();
      this._altKartPaylasKart = {
        _id: Date.now().toString(36),
        _uid: ana?._id,
        vt: ana?.vt || 'FikoYT.sistem.hareketler' as any,
        yol: ana?.yol || 'FikoYT.sistem.hareketler.kalem' as any,
        turu: IKartTuru.KART,
        gizlilik: 'Herkese Açık',
        uygulama: ana?.uygulama,
        kisiler: [],
        veri: { solSimge: { baslik: 'AK', renkArka: '#1890ff', renkYazi: '#fff' }, icerikler: [{ _id: Date.now().toString(36) + 'i', html: '', aciklama: '', sifre: null }] },
      } as any;
    }
    return this._altKartPaylasKart;
  }

  async tiklandiAltKartlar() {
    // this.altKartlarAcik = !this.altKartlarAcik;
    if (this.altKartlarAcik && this.altKartlar.length === 0) {
      await this.altKartlariYukle();
    }
    this.CD.detectChanges();
  }

  async altKartlariYukle() {
    const kartId = this.kart()?._id;
    if (!kartId) return;

    this.altKartlarYukleniyor = true;
    this.CD.detectChanges();

    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
        parametreler: {
          aggregate: [
            {
              $match: {
                vt: "FikoYT.sistem.hareketler",
                _uid: kartId,
                turu: { $in: [IKartTuru.KART, IKartTuru.UYAR_DOKUM] },
                silindi: { $ne: true }
              }
            },
            {
              $lookup: {
                from: "hareketler",
                localField: "veri.icerikler",
                foreignField: "_id",
                as: "veri.icerikler"
              }
            },
            { $sort: { tarih: -1 } },
            { $limit: 20 }
          ]
        },
        onEkOlsun: true,
        depoModu: false,
      });

      const cevap = await istek.isteSync();
      this.altKartlar = cevap?.cevap?.sonuc ?? [];
    } catch (e) {
      this.nzMessage.error('Alt kartlar yüklenemedi');
    } finally {
      this.altKartlarYukleniyor = false;
      this.CD.detectChanges();
    }
  }

  altKartHesaplamaSifirla() {
    const kartId = this.kart()?._id;
    if (kartId) this.altKartHesaplama.cacheSifirla(kartId);
    this.FikoYT.degisti.emit();
  }

  async altKartEkle() {
    const metin = this.yeniAltKartMetni?.trim();
    if (!metin || this.altKartGonderiliyor) return;

    const kartId = this.kart()?._id;
    const kullaniciId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;
    if (!kartId || !kullaniciId) return;

    this.altKartGonderiliyor = true;
    this.CD.detectChanges();

    try {
      const altKartId = this.FikoYT.fikoIdUret();
      const icerikId = this.FikoYT.fikoIdUret();

      const altKart: any = {
        _id: altKartId,
        _uid: kartId,
        vt: "FikoYT.sistem.hareketler",
        turu: IKartTuru.KART,
        sahip: kullaniciId,
        gizlilik: this.kart()?.gizlilik ?? 'Herkese Açık',
        tarih: new Date(),
        veri: {
          solSimge: { baslik: 'AK', renkArka: '#1890ff', renkYazi: '#ffffff' },
          kurdela: { baslik: '' },
          icerikler: [icerikId]
        }
      };

      const icerik: any = {
        _id: icerikId,
        _uid: altKartId,
        vt: "FikoYT.sistem.hareketler",
        html: metin,
        aciklama: '',
        tarih: new Date(),
      };

      // Kart kaydet
      const formData1 = new FormData();
      formData1.append('req_body', JSON.stringify(altKart));
      const kartIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.ekle',
        parametreler: formData1,
        onEkOlsun: true,
        depoModu: false,
      });
      await kartIstek.isteSync();

      // İçerik kaydet
      const formData2 = new FormData();
      formData2.append('req_body', JSON.stringify(icerik));
      const icerikIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.ekle',
        parametreler: formData2,
        onEkOlsun: true,
        depoModu: false,
      });
      await icerikIstek.isteSync();

      // Optimistic: listeye ekle
      altKart.veri.icerikler = [{ _id: icerikId, html: metin }];
      this.altKartlar.unshift(altKart);

      // Yorum sayısını güncelle
      if (this.kart().yorumSayisi == null) this.kart().yorumSayisi = 0;
      this.kart().yorumSayisi++;

      this.yeniAltKartMetni = '';
      this.altKartHesaplamaSifirla();
      this.kartBildirim.bildirimTetikle('alt_kart_eklendi', kartId, this.kart()?.sahip, this.kart()?.veri?.kurdela?.baslik);
      this.nzMessage.success('Alt kart eklendi');
    } catch (e) {
      console.error('Alt kart eklenemedi:', e);
      this.nzMessage.error('Alt kart eklenemedi');
    } finally {
      this.altKartGonderiliyor = false;
      this.CD.detectChanges();
    }
  }

  // Beğenme meşgul durumu
  begenMesgul = false;

  /** NOT: ESKI Beğen İşlemleri - Geriye dönük uyumluluk */
  async tiklandiBegen() {
    if (this.begenMesgul) return; // Çift tıklamayı engelle

    const kartId = this.kart()?._id;
    const kullaniciId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;

    if (!kartId || !kullaniciId) return;

    this.begenMesgul = true;
    this.CD.detectChanges();

    const oncekiBegendi = this.miBegen;

    // Optimistic UI Update
    if (oncekiBegendi) {
      this.kart().begenenler = this.kart().begenenler?.filter(id => id !== kullaniciId) ?? [];
    } else {
      if (!this.kart().begenenler) this.kart().begenenler = [];
      this.kart().begenenler.push(kullaniciId);
    }
    this.CD.detectChanges();

    try {
      // Beğeni hareket kaydı oluştur/güncelle
      const begeniKayit: IKart<any> = {
        vt: "FikoYT.sistem.hareketler",
        yol: "FikoYT.sistem.hareketler.begen",
        sahip: kullaniciId,
        silindi: oncekiBegendi, // Beğeni kaldırıyorsa silindi=true
        turu: IKartTuru.SISTEM,
        veri: kartId,
        tarih: new Date(),
      };

      if (oncekiBegendi) {
        // Beğeniyi kaldırma - mevcut kaydı bul ve silindi=true yap
        const kontrolIstek = new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.aggregate',
          parametreler: {
            aggregate: [
              {
                $match: {
                  vt: "FikoYT.sistem.hareketler",
                  yol: "FikoYT.sistem.hareketler.begen",
                  sahip: kullaniciId,
                  veri: kartId,
                  silindi: { $ne: true }
                }
              }
            ]
          },
          onEkOlsun: true,
          depoModu: false,
        });

        const kontrolCevap = await kontrolIstek.isteSync();
        const mevcutKayit = kontrolCevap?.cevap?.sonuc?.[0];

        if (mevcutKayit?._id) {
          // Silme işlemi - FikoYTIstekKok ile
          const formData = new FormData();
          formData.append('req_body', JSON.stringify({
            _id: mevcutKayit._id,
            silindi: true
          }));

          const silIstek = new FikoYTIstekKok({
            metod: 'POST',
            komut: 'FikoYT.sistem.DB.hareketler.ekle',
            parametreler: formData,
            onEkOlsun: true,
            depoModu: false,
          });

          await silIstek.isteSync();
        }
      } else {
        // Beğeni ekleme - önce silinmiş kayıt var mı kontrol et
        const kontrolIstek = new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.aggregate',
          parametreler: {
            aggregate: [
              {
                $match: {
                  vt: "FikoYT.sistem.hareketler",
                  yol: "FikoYT.sistem.hareketler.begen",
                  sahip: kullaniciId,
                  veri: kartId,
                  silindi: true
                }
              }
            ]
          },
          onEkOlsun: true,
          depoModu: false,
        });

        const kontrolCevap = await kontrolIstek.isteSync();
        const silinmisKayit = kontrolCevap?.cevap?.sonuc?.[0];

        const formData = new FormData();
        if (silinmisKayit?._id) {
          // Silinmiş kayıt varsa geri getir
          formData.append('req_body', JSON.stringify({
            _id: silinmisKayit._id,
            silindi: false
          }));
        } else {
          // Yeni kayıt oluştur
          formData.append('req_body', JSON.stringify(begeniKayit));
        }

        const ekleIstek = new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.ekle',
          parametreler: formData,
          onEkOlsun: true,
          depoModu: false,
        });

        await ekleIstek.isteSync();
      }

      // Beğenenleri aggregate ile güncelle
      const guncelBegenenler = await this.begenenlerListesiniGuncelle(kartId);
      this.kart().begenenler = guncelBegenenler;

      // Güncel beğenenleri kart belgesine kaydet
      await this.kartGuncelle({ begenenler: guncelBegenenler });


    } catch (e) {
      console.error('Beğeni hatası:', e);

      // Rollback - Optimistic UI'ı geri al
      if (oncekiBegendi) {
        if (!this.kart().begenenler) this.kart().begenenler = [];
        this.kart().begenenler.push(kullaniciId);
      } else {
        this.kart().begenenler = this.kart().begenenler?.filter(id => id !== kullaniciId) ?? [];
      }

      this.nzMessage.error('Beğeni işlemi başarısız oldu');
    } finally {
      setTimeout(() => {
        this.begenMesgul = false;
        this.CD.detectChanges();
        this.FikoYT.degisti.emit();
      }, 0);
    }
  }

  async tiklandiBegenenler($event: any) {
    const begenenlerIds = this.kart()?.begenenler ?? [];

    if (begenenlerIds.length === 0) {
      this.nzMessage.info('Henüz kimse beğenmedi');
      return;
    }

    try {
      // MEVCUT SİSTEM: goUyeler.aggregate
      const kullanicilarIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.goUyeler.aggregate',
        parametreler: {
          aggregate: [
            {
              $match: {
                _id: { $in: begenenlerIds }
              }
            }
          ]
        },
        onEkOlsun: true,
        depoModu: false,
      });

      const cevap = await kullanicilarIstek.isteSync();
      const begenenKullanicilar = cevap?.cevap?.sonuc ?? [];

      // MEVCUT MODAL: DialogKisiSeciciComponent
      this.nzModalServis.create({
        nzContent: DialogKisiSeciciComponent,
        nzDraggable: true,
        nzData: {
          kisiler: begenenKullanicilar,
          saltOkunur: true,
          baslik: `Beğenenler (${begenenKullanicilar.length})`
        },
        nzFooter: null,
        nzClosable: true,
        nzMaskClosable: true,
        nzBodyStyle: { padding: '10px' },
        nzStyle: { padding: '0', 'max-width': '500px' },
      });

    } catch (e) {
      console.warn('Beğenenleri getirme hatası:', e);
      this.nzMessage.error('Beğenenler listesi yüklenemedi');
    }
  }

  tiklandiMenu() {
    let test = this.nzModalServis.create({
      nzContent: KartMenuComponent,
      nzDraggable: true,
      nzData: { kart: this.kart() },
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzBodyStyle: { padding: '10px', },
      nzStyle: { padding: '0', 'max-width': '360px' },
    }).afterClose.subscribe((data) => {
      if (data?.aksiyon === 'duzenle') {
        this.kartDuzenlemeModu(data.kart);
      } else if (data?.aksiyon === 'kopyala') {
        if (!this.lisans('kart_klon')) { this.nzMessage.warning('Kart klonlama özelliği lisansınızda yok.'); }
        else {
          this.servisProjeler.kartKopyalaPaylasimaAc(data.kart);
          this.nzMessage.success('Kart kopyası paylaş kutusuna eklendi');
          this.FikoYT.degisti.emit();
        }
      } else if (data?.aksiyon === 'sablonKaydet') {
        if (!this.lisans('sablon_olustur')) { this.nzMessage.warning('Şablon oluşturma özelliği lisansınızda yok.'); }
        else {
          this.kartSablonKaydet(data.kart);
          this.FikoYT.degisti.emit();
        }
      } else if (data?.aksiyon === 'koleksiyonaEkle') {
        this.koleksiyonaEkleDialogAc(data.kart);
        this.FikoYT.degisti.emit();
      }
      this.CD.detectChanges();
      test.unsubscribe();
    });
  }

  /** Kart düzenleme moduna geç */
  duzenlemeModunda = false;
  duzenlemeSayfaNo = 0;
  private _yedekKart: any = null;

  /** Düzenleme için kart kopyası (paylaş kutusu beslemesi) */
  duzenlemeKart: IKart<any> = null;

  kartDuzenlemeModu(kart: any) {
    if (!this.miSahip) return;
    // Yedek al (iptal için)
    this._yedekKart = JSON.parse(JSON.stringify(kart));
    // Paylaş kutusu için kart kopyası oluştur
    this.duzenlemeKart = JSON.parse(JSON.stringify(kart));
    // veri.icerikler yoksa icerikler'den al
    if (!this.duzenlemeKart.veri) this.duzenlemeKart.veri = {} as any;
    if (!this.duzenlemeKart.veri.icerikler && this.duzenlemeKart.icerikler) {
      this.duzenlemeKart.veri.icerikler = JSON.parse(JSON.stringify(this.duzenlemeKart.icerikler));
    }
    // Sunucudaki medyalar için orijinal türe dönüştür + dosyaObjectUrl ata
    // Düzenleme modunda EKREFERANS → orijinal türe çevir (Referans etiketi görünmez)
    const fapiUrl = this.FikoYT.fapiUrl;
    for (const icerik of (this.duzenlemeKart.veri?.icerikler ?? [])) {
      const ek = icerik?.ek;
      if (!ek?._id || ek.dosya) continue;

      // EKREFERANS → orijinal türe geri dönüştür
      if (ek.turu === 'EKREFERANS' && ek.bilgiler?.orijinalTuru) {
        ek.turu = ek.bilgiler.orijinalTuru;
      }

      // dosyaObjectUrl yoksa sunucu URL'sini ata (değişmemiş dosya tekrar yüklenmez)
      if (!ek.dosyaObjectUrl) {
        const dosyaTemel = fapiUrl + '/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.' + ek._id;
        switch (ek.turu) {
          case 'RESIM':
            ek.dosyaObjectUrl = dosyaTemel + '.resim?boyut=760x?&t=0';
            break;
          case 'VIDEO':
          case 'SES':
          case 'DOSYA':
            ek.dosyaObjectUrl = dosyaTemel + '.dosya';
            break;
        }
      }
    }
    // Paylaş kutusunun aktifIcerikNo'sunu sıfırla
    this.servisProjeler.aktifIcerikNo = 0;
    this.duzenlemeModunda = true;
    this.duzenlemeSayfaNo = 0;
    this.CD.detectChanges();
  }

  /** Düzenleme modunda sayfa ekle */
  duzenlemeSayfaEkle() {
    const kart = this.kart();
    if (!kart) return;
    const icerikler = kart.veri?.icerikler || kart.icerikler;
    if (!icerikler) return;
    icerikler.push({ _id: this.FikoYT.fikoIdUret(), aciklama: '', html: '', ek: null, sifre: null });
    this.duzenlemeSayfaNo = icerikler.length - 1;
    this.CD.detectChanges();
  }

  /** Düzenleme modunda aktif sayfayı sil */
  duzenlemeSayfaSil() {
    const kart = this.kart();
    if (!kart) return;
    const icerikler = kart.veri?.icerikler || kart.icerikler;
    if (!icerikler || icerikler.length <= 1) return;
    icerikler.splice(this.duzenlemeSayfaNo, 1);
    if (this.duzenlemeSayfaNo >= icerikler.length) {
      this.duzenlemeSayfaNo = icerikler.length - 1;
    }
    this.CD.detectChanges();
  }

  /** Düzenlemeyi kaydet */
  async kartDuzenlemeKaydet() {
    const kart = this.kart();
    if (!kart?._id || !this.miSahip || this.duzenlemeMesgul) return;

    this.duzenlemeMesgul = true;
    this.CD.detectChanges();

    try {
      const icerikler = kart.veri?.icerikler || kart.icerikler || [];

      // 1) Her içeriği KENDİ belgesine upsert et (paylaşım akışı formatı: _uid = kartId)
      //    Serileştirilemez/yerel alanları temizle
      for (const ic of icerikler) {
                if (!ic?._id) continue;
        const belge = JSON.parse(JSON.stringify(ic));
        belge._uid = kart._id;
        delete belge.ek?.dosyaObjectUrl;
        delete belge.ek?.objectUrl;
        delete belge.ek?.resimObjectUrl;
        delete belge.ek?.dosya;
        delete belge.ek?.resim;   // Blob: JSON.stringify'de {} çıkar; sunucu tarafından yüklenir
        delete belge.ek?._sablonDosyalar;
        const fd = new FormData();
        fd.append('req_body', JSON.stringify(belge));

        // 3D (STP/WRL) akışı entegrasyonu: STP fotoğrafı (ek.resim) blob'unu
        // `resimid` anahtarıyla, model/dosya (ek.dosya) blob'unu `ek._id` anahtarıyla yükle.
        // (ikart-kutusu.paylasTiklandi'daki kartDerinKopya + forFormData.dosyalar
        //  takıntısıyla aynı GridFS routing'i — bu iki yolda tutarlı olmalı.)
        const ek = ic?.ek;
        if (ek?.resim instanceof Blob) {
          fd.append(ek.resimid || (ek._id + '_resim'), ek.resim, 'stp-goruntu.png');
        }
        if (ek?.dosya instanceof File || ek?.dosya instanceof Blob) {
          fd.append(String(ek._id), ek.dosya, ek.dosya.name || 'dosya');
        }

        await new FikoYTIstekKok({
          metod: 'POST',
          komut: 'FikoYT.sistem.DB.hareketler.ekle',
          onEkOlsun: true,
          parametreler: fd,
          depoModu: false,
          gecikmeliCevap: false,
        }).isteSync();
      }

      // 2) Kart dokümanına SADECE id listesi yaz (veri.icerikler DB'de id dizisidir;
      //    okuma sırasında $lookup ile doldurulur — tam obje yazmak hydrate'i bozar)
      const idList = icerikler.map((c: any) => c?._id).filter(Boolean);

      const istek = await FikoYTIstekKok.request({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.guncelle',
        parametreler: {
          filter: { _id: kart._id },
          set: {
            icerikler: idList,
            'veri.icerikler': idList,
            parametreler: kart.parametreler,
            etiketler: kart.etiketler,
            duzenlenmeTarihi: new Date()
          }
        },
        tekrarSayisi: 3,
        oncelik: 5,
        gecikmeliCevap: false,
      });
      const cevap: any = await istek.isteSync();
      if (cevap?.cevap?.sonuc) {
        this.nzMessage.success('Kart güncellendi');
        kart.duzenlenmeTarihi = new Date();
        if (kart.veri?.icerikler) kart.veri.icerikler = [...icerikler];
        kart.icerikler = [...icerikler];
        this.duzenlemeModunda = false;
        this.duzenlemeKart = null;
        this.duzenlemeSayfaNo = 0;
        this._yedekKart = null;
        this.kartGuncellendi.emit({ ...kart });
        this.canliGuncelleme.guncellemeYayin(kart._id, { ...kart });
        this.kartBildirim.bildirimTetikle('kart_guncellendi', kart._id, kart.sahip, kart.veri?.kurdela?.baslik);
        const mentionHtmller = (icerikler || []).map((i: any) => i.html || '').filter((h: string) => h.includes('data-user-id'));
        if (mentionHtmller.length > 0) this.kartBildirim.mentionBildirimGonder(kart._id, kart.veri?.kurdela?.baslik || '', mentionHtmller);
        this.CD.detectChanges();
        this.FikoYT.degisti.emit();
      } else {
        this.nzMessage.error('Güncelleme başarısız');
        this.CD.detectChanges();
      }
    } catch (e) {
      this.nzMessage.error('Güncelleme sırasında bir hata oluştu');
    } finally {
      this.duzenlemeMesgul = false;
      this.CD.detectChanges();
    }
  }

  /** Düzenlemeyi iptal et */
  kartDuzenlemeIptal() {
    // Kartı yedeğe geri al
    const kart = this.kart();
    if (kart && this._yedekKart) {
      const yedek = JSON.parse(JSON.stringify(this._yedekKart));
      if (kart.veri) Object.assign(kart.veri, yedek.veri || {});
      if (yedek.icerikler) kart.icerikler = yedek.icerikler;
      if (yedek.etiketler) kart.etiketler = yedek.etiketler;
      if (yedek.kisiler) kart.kisiler = yedek.kisiler;
      if (yedek.gizlilik) kart.gizlilik = yedek.gizlilik;
      if (yedek.parametreler) kart.parametreler = yedek.parametreler;
    }
    this.duzenlemeModunda = false;
    this.duzenlemeKart = null;
    this.duzenlemeSayfaNo = 0;
    this._yedekKart = null;
    this.CD.detectChanges();
    this.FikoYT.degisti.emit();
  }

  /** Paylaş kutusundan düzenleme başarılı olduğunda */
  kartDuzenlemeBasarili(guncellenmisKart: any) {
    const kart = this.kart();
    const kaynak = this.duzenlemeKart;
    if (kart && kaynak) {
      if (kaynak.veri?.icerikler) {
        const yeniIcerikler = [...kaynak.veri.icerikler];
        if (kart.veri) kart.veri.icerikler = yeniIcerikler;
        kart.icerikler = yeniIcerikler;
      }
      if (kaynak.etiketler) kart.etiketler = [...kaynak.etiketler];
      if (kaynak.kisiler) kart.kisiler = [...kaynak.kisiler];
      if (kaynak.gizlilik) kart.gizlilik = kaynak.gizlilik;
      if (kaynak.parametreler) kart.parametreler = [...kaynak.parametreler];
      kart.duzenlenmeTarihi = new Date();
    }
    this.duzenlemeModunda = false;
    this.duzenlemeKart = null;
    this.duzenlemeSayfaNo = 0;
    this._yedekKart = null;
    this.kartGuncellendi.emit({ ...kart });
    if (kart?._id) this.canliGuncelleme.guncellemeYayin(kart._id, { ...kart });
    if (kart?._id) this.kartBildirim.bildirimTetikle('kart_guncellendi', kart._id, kart.sahip, kart.veri?.kurdela?.baslik);
    this.CD.detectChanges();
    this.FikoYT.degisti.emit();
  }

  /** Şablon olarak kaydet */
  async kartSablonKaydet(kart: any) {
    if (!kart?.parametreler?.length) {
      this.nzMessage.warning('Bu kartta parametre yok, şablon oluşturulamaz');
      return;
    }

    const sablon = {
      turu: IKartTuru.SABLON,
      sahip: this.FikoYT.kimlik.aktifKullaniciJson()?._id,
      parametreler: kart.parametreler.map((p: any) => ({ ...p, degeri: null })),
      etiketler: kart.etiketler ?? [],
      gizlilik: 'Sadece Ben',
      veri: {
        sablonAdi: 'Şablon - ' + (kart.icerikler?.[0]?.aciklama?.substring(0, 30) || 'İsimsiz'),
        sablonKategori: 'ozel',
      },
      tarih: new Date()
    };

    const istek = await FikoYTIstekKok.request({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.kalem',
      parametreler: sablon,
      tekrarSayisi: 3,
      oncelik: 5,
      gecikmeliCevap: false,
    });
    const cevap: any = await istek.isteSync();
    if (cevap?.cevap?.sonuc) {
      this.nzNotification.success("Başarılı", 'Şablon kaydedildi');
    } else {
      this.nzNotification.error("Hata", 'Şablon kaydedilemedi');
    }
  }

  tiklandiDialogGizlilik() {
    const saltOkunur = !this.miSahip;
    let test = this.nzModalServis.create({
      nzContent: DialogGizlilikSeciciComponent,
      nzDraggable: false,
      nzData: { gizlilik: this.kart().gizlilik, liste: this.kart().gizlilikListe, saltOkunur },
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: saltOkunur,
      nzBodyStyle: { padding: '10px' },
      nzStyle: { padding: '0', 'max-width': '360px' },
      nzMaskStyle: { 'background-color': 'rgba(0, 0, 0, 0.3)' }
    }).afterClose.subscribe((data) => {
      if (data && this.miSahip) {
        this.kart().gizlilik = data?.gizlilik;
        this.kart().gizlilikListe = data?.liste ?? [];
        this.kartGuncelle({ gizlilik: data.gizlilik, gizlilikListe: data.liste ?? [] });
      }
      test.unsubscribe();
      this.CD.detectChanges();
    });
  }

  tiklandiDialogKisiSecici() {
    const saltOkunur = !this.miSahip;
    let test = this.nzModalServis.create({
      nzContent: DialogKisiSeciciComponent,
      nzDraggable: true,
      nzData: { kisiler: this.kart().kisiler ?? [], saltOkunur },
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: saltOkunur,
      nzBodyStyle: { padding: '10px' },
      nzStyle: { padding: '0', 'max-width': '360px' },
    }).afterClose.subscribe((data) => {
      if (data && this.miSahip) {
        this.kart().kisiler = data?.kisiler ?? this.kart().kisiler;
        this.kartGuncelle({ kisiler: this.kart().kisiler });
      }
      test.unsubscribe();
      this.CD.detectChanges();
    });
  }

  tiklandiDialogEtiketler() {
    const saltOkunur = !this.miSahip;
    let test = this.nzModalServis.create({
      nzContent: DialogEtiketlerComponent,
      nzDraggable: true,
      nzData: { etiketler: this.kart().etiketler ?? [], saltOkunur },
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: saltOkunur,
      nzBodyStyle: { padding: '10px' },
      nzStyle: { padding: '0', 'max-width': '360px' },
    }).afterClose.subscribe((data) => {
      if (data && this.miSahip) {
        this.kart().etiketler = data;
        this.kartGuncelle({ etiketler: data });
      }
      test.unsubscribe();
      this.CD.detectChanges();
    });
  }

  kartGuncellePublic(degerler: Record<string, any>) {
    this.kartGuncelle(degerler);
  }

  async tiklandiKonumDuzenle() {
    if (!this.miSahip) return;
    const { HaritaYerSeciciComponent } = await import('@/src/app/uygulamalar/soyagaci/harita-yer-secici/harita-yer-secici.component');
    const sub = this.nzModalServis.create({
      nzTitle: 'Konum Seç',
      nzContent: HaritaYerSeciciComponent,
      nzWidth: 520,
      nzFooter: null,
      nzData: {
        mevcutYer: this.kart()?.konum?.yerIsmi || '',
        mevcutKoordinat: this.kart()?.konum?.koordinat || null,
      },
    }).afterClose.subscribe((sonuc: any) => {
      if (sonuc?.yerIsmi) {
        this.kart().konum = { yerIsmi: sonuc.yerIsmi, koordinat: sonuc.koordinat };
        this.kartGuncelle({ konum: this.kart().konum });
        this.CD.detectChanges();
      }
      sub.unsubscribe();
    });
  }

  tiklandiBayrakDuzenle() {
    if (!this.miSahip) return;

    let test = this.nzModalServis.create({
      nzContent: FikoYTBayrakEditor,
      nzDraggable: true,
      nzData: { bayrak: this.kart()?.veri?.solSimge },
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzBodyStyle: { padding: '10px' },
      nzStyle: { padding: '0', 'max-width': '360px' },
    }).afterClose.subscribe((data) => {
      if (data?.bayrak) {
        if (!this.kart().veri) this.kart().veri = {};
        this.kart().veri.solSimge = data.bayrak;
        this.kartGuncelle({ 'veri.solSimge': data.bayrak });
      }
      test.unsubscribe();
      this.CD.detectChanges();
    });
  }

  // ─── Koleksiyon Sistemi ───
  async koleksiyonaEkleDialogAc(kart: any) {
    if (!kart?._id) return;

    // Mevcut koleksiyonları getir
    const koleksiyonIstek = new FikoYTIstekKok({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.aggregate',
      parametreler: {
        aggregate: [
          { $match: { vt: 'FikoYT.sistem.hareketler', yol: 'FikoYT.sistem.hareketler.koleksiyon', silindi: { $ne: true } } },
          { $match: { sahip: this.FikoYT.kimlik.aktifKullaniciJson()?._id } },
          { $sort: { _id: -1 } },
          { $limit: 50 },
        ]
      },
      onEkOlsun: true,
      depoModu: false,
    });
    const cevap: any = await koleksiyonIstek.isteSync();
    const mevcutKoleksiyonlar: any[] = cevap?.cevap?.sonuc ?? [];

    // NzModal ile confirm dialog
    const yeniKoleksiyonAdi = { value: '' };
    const seciliKoleksiyon = { value: null as any };

    const modalRef = this.nzModalServis.create({
      nzTitle: 'Koleksiyona Ekle',
      nzContent: `
        <div style="font-size:13px;">
          ${mevcutKoleksiyonlar.length > 0 ? `
            <div style="margin-bottom:12px;">
              <label style="font-weight:500;display:block;margin-bottom:4px;">Mevcut Koleksiyonlar</label>
              <div style="max-height:180px;overflow-y:auto;border:1px solid var(--py-kenarlik);border-radius:4px;">
                ${mevcutKoleksiyonlar.map((k, i) => `
                  <div class="koleksiyon-item" data-index="${i}"
                       style="padding:8px 12px;cursor:pointer;border-bottom:1px solid var(--py-bolucu);transition:background 0.2s;"
                       onmouseover="this.style.background='var(--py-arkaplan-hover)'"
                       onmouseout="this.style.background=this.classList.contains('secili')?'var(--py-arkaplan-aktif)':''"
                  >
                    <i class="folder icon" style="color:#faad14;"></i>
                    <strong>${k.veri?.koleksiyonAdi ?? 'İsimsiz'}</strong>
                    <small style="color:var(--py-metin-ucuncul);margin-left:8px;">(${k.veri?.kartlar?.length ?? 0} kart)</small>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
          <div>
            <label style="font-weight:500;display:block;margin-bottom:4px;">Yeni Koleksiyon Oluştur</label>
            <input id="yeniKoleksiyonInput" type="text" class="ui input" placeholder="Koleksiyon adı..."
                   style="width:100%;padding:6px 10px;border:1px solid var(--py-input-kenarlik);border-radius:4px;font-size:13px;background:var(--py-input-arka);color:var(--py-input-metin);" />
          </div>
        </div>
      `,
      nzOkText: 'Ekle',
      nzCancelText: 'İptal',
      nzBodyStyle: { padding: '16px' },
      nzStyle: { 'max-width': '380px' },
      nzOnOk: async () => {
        const input = document.getElementById('yeniKoleksiyonInput') as HTMLInputElement;
        const yeniAd = input?.value?.trim();
        const seciliEl = document.querySelector('.koleksiyon-item.secili');
        const seciliIdx = seciliEl ? parseInt(seciliEl.getAttribute('data-index') ?? '-1') : -1;

        if (seciliIdx >= 0) {
          // Mevcut koleksiyona ekle
          const koleksiyon = mevcutKoleksiyonlar[seciliIdx];
          if (!koleksiyon.veri.kartlar) koleksiyon.veri.kartlar = [];
          if (koleksiyon.veri.kartlar.includes(kart._id)) {
            this.nzMessage.warning('Kart zaten bu koleksiyonda');
            return;
          }
          koleksiyon.veri.kartlar.push(kart._id);
          const formData = new FormData();
          formData.append('req_body', JSON.stringify({
            _id: koleksiyon._id,
            'veri.kartlar': koleksiyon.veri.kartlar
          }));
          const guncelle = new FikoYTIstekKok({
            metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle',
            parametreler: formData, onEkOlsun: true, depoModu: false,
          });
          await guncelle.isteSync();
          this.nzMessage.success(`"${koleksiyon.veri.koleksiyonAdi}" koleksiyonuna eklendi`);
        } else if (yeniAd) {
          // Yeni koleksiyon oluştur
          const formData = new FormData();
          formData.append('req_body', JSON.stringify({
            vt: 'FikoYT.sistem.hareketler',
            yol: 'FikoYT.sistem.hareketler.koleksiyon',
            turu: 'SISTEM',
            veri: {
              koleksiyonAdi: yeniAd,
              kartlar: [kart._id]
            }
          }));
          const ekle = new FikoYTIstekKok({
            metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.ekle',
            parametreler: formData, onEkOlsun: true, depoModu: false,
          });
          await ekle.isteSync();
          this.nzMessage.success(`"${yeniAd}" koleksiyonu oluşturuldu`);
        } else {
          this.nzMessage.warning('Bir koleksiyon seçin veya yeni ad girin');
          return false;
        }
      },
    });

    // Koleksiyon item tıklama olayını bağla
    setTimeout(() => {
      const items = document.querySelectorAll('.koleksiyon-item');
      items.forEach(item => {
        item.addEventListener('click', () => {
          items.forEach(i => { (i as HTMLElement).classList.remove('secili'); (i as HTMLElement).style.background = ''; });
          (item as HTMLElement).classList.add('secili');
          (item as HTMLElement).style.background = '#bae7ff';
        });
      });
    }, 100);
  }

  private async kartGuncelle(degerler: Record<string, any>) {
    const kartId = this.kart()?._id;
    if (!kartId) return;

    try {
      const guncelleme: any = { _id: kartId, ...degerler };
      const formData = new FormData();
      formData.append('req_body', JSON.stringify(guncelleme));

      const guncelleIstek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.ekle',
        parametreler: formData,
        onEkOlsun: true,
        depoModu: false,
      });

      await guncelleIstek.isteSync();
    } catch (e) {
      console.warn('Kart güncelleme hatası:', e);
      this.nzMessage?.error('Güncelleme kaydedilemedi');
    }
    this.FikoYT.degisti.emit();
  }

  // ─── Sayfa Bilgi Balon (kisi-balon-v2 ile) ───
  sayfaBalonAcik = false;
  sayfaBalonKisi: any = null;

  sayfaBalonToggle(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (this.sayfaBalonAcik) {
      this.sayfaBalonAcik = false;
      this.CD.detectChanges();
      return;
    }
    const sb = this.kart()?.ayarlar?._sayfaBilgi;
    if (!sb?.hedefId) return;

    this.sayfaBalonKisi = {
      id: sb.hedefId,
      kisiIsmi: sb.hedefIsim || '',
      soyagaciKisiId: sb.hedefId,
    };
    this.sayfaBalonAcik = true;
    this.CD.detectChanges();
  }

  sayfaBalonGorunurlukDegisti(gorunur: boolean) {
    if (!gorunur && this.sayfaBalonAcik) {
      this.sayfaBalonAcik = false;
      this.CD.detectChanges();
    }
  }

}
